import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout } from "@/components/site/Layout";
import { useServicesList } from "@/lib/services";
import { ArrowRight } from "lucide-react";
import { useT, useMedia } from "@/lib/i18n";
import heroImg from "@/assets/bosque-hero.jpg";

export const Route = createFileRoute("/services/areas")({
  component: Areas,
  head: () => ({
    meta: [
      { title: "Actividades — Bosque Suspendido" },
      { name: "description", content: "Todas las actividades del Bosque Suspendido: 11 circuitos en los árboles, tirolinas, salto al vacío, paintball, lucha de arqueros, tiro con arco, escalada, banana jump, GelBall, rocódromo, boulder, team building y bosque nocturno." },
      { property: "og:title", content: "Actividades — Bosque Suspendido" },
      { property: "og:description", content: "Descubre todas las aventuras que puedes vivir en el pinar." },
      { property: "og:url", content: "https://bosque-suspendido.lovable.app/services/areas" },
    ],
    links: [{ rel: "canonical", href: "https://bosque-suspendido.lovable.app/services/areas" }],
  }),
});

function Areas() {
  const { tk } = useT();
  const services = useServicesList();
  const hero = useMedia("services.hero", heroImg, "Actividades del Bosque Suspendido");

  return (
    <Layout>
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <span className="text-[10px] tracking-[0.4em] uppercase text-gold">{tk("areas.kicker", "Actividades")}</span>
          <h1 className="font-serif text-4xl md:text-5xl mt-4">
            {tk("areas.title.a", "Todo lo que puedes ")}
            <em className="not-italic gold-gradient">{tk("areas.title.b", "vivir en el pinar")}</em>
          </h1>
          <p className="mt-5 text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            {tk("areas.intro", "Más de una docena de actividades para todas las edades, desde los 2 años. Combínalas a tu medida y vive un día completo de aventura.")}
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 items-start">
          {services.map((s, i) => (
            <div key={s.slug} className="group rounded-2xl border border-gold/15 bg-card/30 overflow-hidden hover:border-gold/60 hover:bg-card/60 transition-colors flex flex-col">
              {s.imageUrl && (
                <div className="aspect-[4/3] overflow-hidden bg-card">
                  <img
                    src={s.imageUrl}
                    alt={s.name}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                  />
                </div>
              )}
              <div className="p-7 flex flex-col flex-1">
                <div className="text-[10px] tracking-[0.3em] uppercase text-gold/70">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <h2 className="font-serif text-2xl mt-3 group-hover:text-gold transition-colors">{s.name}</h2>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{s.desc}</p>

                {s.subcategories && s.subcategories.length > 0 && (
                  <div className="mt-6 pt-6 border-t border-gold/15">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-gold/70 mb-4">
                      {tk("areas.includes", "Incluye")}
                    </div>
                    <ul className="space-y-3">
                      {s.subcategories.map((sub) => (
                        <li key={sub.name} className="flex gap-3">
                          <span className="mt-2 h-1.5 w-1.5 rounded-full bg-gold shrink-0" aria-hidden />
                          <div>
                            <div className="text-sm font-medium text-foreground">{sub.name}</div>
                            <div className="text-xs text-muted-foreground leading-relaxed mt-0.5">{sub.desc}</div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <Link to="/contact" className="mt-6 inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-gold/80 group-hover:text-gold">
                  {tk("areas.contact", "Reservar")} <ArrowRight size={12} />
                </Link>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section
        className="border-t border-gold/20 relative bg-cover bg-center"
        style={{ backgroundImage: `url(${hero.url})` }}
      >
        <div className="absolute inset-0 bg-black/70" />
        <div className="max-w-5xl mx-auto px-6 py-20 text-center relative z-10">
          <h2 className="font-serif text-4xl md:text-5xl text-white">{tk("areas.cta.title", "¿Listo para vivir la aventura?")}</h2>
          <p className="mt-5 text-white/80">
            {tk("areas.cta.text", "Combina actividades, organiza un grupo o reserva tu día. Recuerda: la reserva previa es obligatoria y gratuita.")}
          </p>
          <Link to="/contact" className="mt-8 inline-flex wood-btn px-8 py-4 text-xs tracking-[0.3em] uppercase">
            {tk("areas.cta.button", "Reservar ahora")}
          </Link>
        </div>
      </section>
    </Layout>
  );
}
